// WHY a key stopped working — the reassignment trigger, and every marker this
// platform grades a provider body by. See `../llm-credential.ts` for the file docblock.

import { z } from 'zod';
import { LLM_PROVIDER_CODE_MAX_CHARS } from './provider-code';

// ─── key-failure reasons (reassignment trigger) ──────────────────────────────

/**
 * Why a key was dropped mid-run — the runner reports this as a fleet event and
 * the assigner reassigns to the next key (`skipKeyIds`) rather than failing the
 * task (arc 101 slice 04), or a live run hops to a sibling key over `fleet.req`
 * `llm_credential_next` and keeps its transcript (#6098).
 *
 * - `credits_exhausted`    — the key has no quota/credit left (402 / hard 403). The
 *   one WALL ({@link isKeyWall}).
 * - `rate_limited`         — transient throttle (429); the key may recover later.
 * - `provider_unavailable` — 5xx / transport timeout that survived the in-step retry
 *   budget (#6098). The provider's fault, not the credential's.
 * - `slow`                 — the review lane's primary dial outlived
 *   `REVIEW_SLOW_CALL_MS` and a sibling was hedged (#6098). Nothing failed.
 *
 * The last three are NON-WALL hops: a short cool, never the task's skip set, never a
 * key-row status write ({@link isLlmKeyRowCoolingReason}), never a maintainer email.
 */
export const LlmKeyFailureReason = {
  CreditsExhausted: 'credits_exhausted',
  RateLimited: 'rate_limited',
  ProviderUnavailable: 'provider_unavailable',
  Slow: 'slow',
} as const;
export const LlmKeyFailureReasonSchema = z.enum(LlmKeyFailureReason);
export type LlmKeyFailureReason = z.infer<typeof LlmKeyFailureReasonSchema>;

/**
 * THE ONE predicate for "cool hard, record in the task's skip set, email the
 * maintainer" (#6098). Only a spent wallet: it cannot be waited out inside a run, so
 * the run must never be handed that key again. Every other reason is a NON-WALL hop —
 * the key is healthy enough to come back in a minute, and a skip-set entry would keep
 * a single-key account off its only working key for a quarter of an hour.
 */
export const isKeyWall = (r: LlmKeyFailureReason): boolean =>
  r === LlmKeyFailureReason.CreditsExhausted;

/**
 * The reasons `byok_keys.status` can carry (`BYOKStatus`, same spelling): only these
 * may be written to the key ROW (`markKeyCooling`). `provider_unavailable` and `slow`
 * are facts about one call, not about the credential, and have no status to land in —
 * `status` is a bare `text` column, so without this type a `slow` would compile and
 * write a status no reader knows.
 */
export type LlmKeyRowCoolingReason =
  | typeof LlmKeyFailureReason.CreditsExhausted
  | typeof LlmKeyFailureReason.RateLimited;

export function isLlmKeyRowCoolingReason(r: LlmKeyFailureReason): r is LlmKeyRowCoolingReason {
  return r === LlmKeyFailureReason.CreditsExhausted || r === LlmKeyFailureReason.RateLimited;
}

/**
 * What a `rate_limited` key failure rests on (2026-09-28) — the reason is the COOL, this
 * is the FACT behind it, and a consumer that learns from the fact reads this:
 *
 * - `provider_refused`  — the provider ANSWERED with a rate-limit refusal (an HTTP 429
 *   graded `rate_limited`; on a box, the rate-limit gate's `KeyRateLimitedError`).
 * - `transport_timeout` — NO ANSWER: the request timed out in the transport before the
 *   provider said anything (`ProviderTransportError`, fault `timed_out`).
 */
export const LlmKeyFailureEvidence = {
  ProviderRefused: 'provider_refused',
  TransportTimeout: 'transport_timeout',
} as const;
export const LlmKeyFailureEvidenceSchema = z.enum(LlmKeyFailureEvidence);
export type LlmKeyFailureEvidence = z.infer<typeof LlmKeyFailureEvidenceSchema>;

/**
 * Exhaustion markers providers put in a 429 body when the wallet — not the
 * throughput window — is what ran out. Every alternative is EXHAUSTION prose a
 * throttle would never use; none of them is a bare `limit` or `rate`, because a
 * marker that fires on ordinary throttle copy converts a 60s step-aside into a
 * 300s one for a key that was working fine.
 *
 *   - `insufficient_quota` — OpenAI's machine-readable code.
 *   - `insufficient credit|balance` / `credit balance` — Anthropic ("credit
 *     balance is too low"), z.ai ("Insufficient balance or no resource package"),
 *     OpenRouter ("Insufficient credits").
 *   - `exceeded your current quota` / `quota exceeded` — OpenAI prose, and the
 *     generic wording several OpenAI-compatible gateways copy.
 *   - `billing hard limit` — an account ceiling, not a per-minute one. The word
 *     `billing` is NEVER matched alone: Kimi's transient copy says "for this
 *     billing cycle", so a bare `billing` would promote a throttle.
 *   - `out of credits` / `payment required` — self-describing.
 *   - `access_terminated_error` — Kimi's code, kept because a machine-authored
 *     code is the better signal WHEN IT IS SENT. Measured 2026-08-29 over four
 *     days: it is sent on ZERO of the 314 bodies that carry Kimi's usage-limit
 *     prose. Preferring the code was right and preferring it EXCLUSIVELY left
 *     the whole population ungraded — so the prose marker below is what actually
 *     carries this provider, and the code stays as the cheaper match if Kimi
 *     ever emits it.
 *   - `reached … usage limit` (either word order) — a REACHED LIMIT, which is
 *     the property, rather than any one vendor's sentence. Measured over the
 *     same four days, Kimi sends three phrasings and the previous vocabulary
 *     matched none of them:
 *       "You've reached your weekly (7-day) usage limit"          238
 *       "You've reached your 5-hour usage limit"                   52
 *       "You've reached your usage limit for this billing cycle"   24
 *     All three say the same thing with the words in a different order, which is
 *     exactly why appending literal number four would have been the wrong fix:
 *     the next window Kimi invents reorders them again and silently returns 314
 *     failures to the residual bucket with every pinned test still green.
 *
 *     WHAT IT MATCHES: `reached` and `usage limit` co-occurring within 40
 *     characters, in either order. WHAT IT REJECTS, deliberately: bare `usage
 *     limit` with no `reached` — "your usage limit is 1,000 rpm" is a
 *     DESCRIPTION of a ceiling, not a report of hitting one, and matching it
 *     would promote every provider that documents its limits in an error body.
 *     The 40-character bound is what keeps the two words in one clause instead
 *     of matching "reached" in one sentence and "usage limit" three sentences
 *     later.
 *
 *     THIS REVERSES A DELIBERATE EXCLUSION and the reversal is evidence-backed.
 *     `usage limit` was refused outright because Kimi's "for this billing cycle"
 *     copy was read as transient. It is not: a billing-cycle ceiling clears when
 *     the cycle rolls, and a WEEKLY one — 238 of the 314, the dominant case, and
 *     unknown when that call was made — clears in up to seven days. Neither is
 *     reachable by the 60s `rate_limited` cooldown, and grading them
 *     `rate_limited` is the infinite-retry loop this function's own risk
 *     analysis (below) says to lean away from.
 *   - `usage limit reached` — MiniMax: "Token Plan usage limit reached: Upgrade
 *     your Token Plan or purchase Credits for more usage. (2056)", served as a
 *     429 on a hard plan ceiling that no amount of waiting clears. Kept as its
 *     own alternative because MiniMax puts the words in the opposite order from
 *     Kimi and the sibling marker's 40-character window is anchored on `reached`
 *     coming FIRST; the two together are what make the vocabulary order-free.
 *   - `purchase credits` — the same MiniMax body's remedy. A provider that tells
 *     you to BUY something is not asking you to slow down.
 *   - `limit exhausted` — z.ai's coding plan, live 2026-09-03: `429 {"code":"1310",
 *     "message":"Weekly/Monthly Limit Exhausted. Your limit will reset at
 *     2026-09-08 10:02:31"}`. A plan window that resets in five days matched
 *     NOTHING above, so the structured-output probe filed it as a transient
 *     `429` and `cron.byok_reverify` re-probed it every two minutes for the rest
 *     of the week (#3158). The only body on this platform that also NAMES its
 *     reset time; `parseLlmQuotaResetAt` (`./quota-reset`) reads it.
 *   - `quota … exhausted` — Alibaba's Token Plan, live 2026-09-11 on `tesote`:
 *     "Your token-plan 1-week quota has been exhausted. The quota will reset at
 *     09-18 16:26:00 UTC." The SAME FACT as z.ai's `limit exhausted` with the
 *     other noun, and it matched nothing: `quota exceeded` carries the other verb.
 *     The cost was the whole point of this vocabulary — the account's scout ran a
 *     74-minute survey, died on this wall at the plan call, shipped a degenerate
 *     plan, and the key stayed at the head of the pool because nothing cooled it,
 *     with a healthy sibling key unfenced beside it. Bounded interior so the
 *     copula varies ("is"/"has been") while a sentence that merely DESCRIBES a
 *     quota still matches nothing.
 *   - `key limit exceeded` / `requires more credits` / `can only afford` /
 *     `free-models-per-day` — OpenRouter, live 2026-09-20 on `tesote` (plan 101
 *     K1): a `403 Key limit exceeded (daily limit)` matched nothing, so the
 *     in-cluster lane graded a spent per-key cap `byok_invalid` and paused the
 *     account's only key. The cap window varies (`daily`/`weekly`/`monthly`/
 *     `total`), hence the stem. `requires more credits … can only afford N` is
 *     OpenRouter's 402 when the balance cannot cover `max_tokens`, and
 *     `free-models-per-day` its daily free-tier cap ("Add 10 credits to unlock").
 *     None is throttle copy: OpenRouter's per-minute 429 reads `Rate limit
 *     exceeded: limit_rpm/…`, which matches none of them.
 *   - `Throttling.AllocationQuota` — Alibaba's Token Plan CODE, captured by the
 *     prod verify of key `cf1eb8b6` over the Anthropic wire, 2026-09-22: `429
 *     {"code":"Throttling.AllocationQuota","message":"Your token-plan 1-week quota
 *     has been exhausted. The quota will reset at 09-25 16:26:00 UTC.",…}` (plan
 *     102 slice 03 R8). The prose already matches `quota … exhausted`; the code is
 *     the machine-authored signal, so a reworded message still grades exhausted.
 *     Only this code: Alibaba's `Throttling.RateQuota` is a per-minute throttle.
 *   - `<upstream>:usage-limit` — the in-cluster router's relay of an upstream's spent
 *     usage window, live 2026-10-09 on router/k3 key `590f9aa1`: `upstream rate limited
 *     (kimi:usage-limit-5-hour)` for ~5 h. It is Kimi's "You've reached your 5-hour usage
 *     limit" (the `reached … usage limit` marker above) in MACHINE form, so it is the same
 *     wall; read as the bare `upstream rate limited` throttle it graded a 60 s
 *     `rate_limited`, nothing durable cooled the key, and every in-process screen on the
 *     account died on it while the assigner kept dispatching onto it. Anchored on the
 *     provider-qualified token (`<name>:usage-limit`), never a bare `usage limit`, which
 *     can describe a ceiling rather than report hitting one.
 *
 * THIS IS THE ONLY COPY, as of 2026-08-29, and that is a recent and hard-won
 * property. It used to be one of three: `CREDITS_PATTERN` in
 * `packages/agent/src/loop.ts` (deleted) and a substring list in
 * `apps/runner/src/modes/worker/review-brain.ts`. They drifted exactly as a
 * duplicated vocabulary does — neither of the other two carried the MiniMax
 * markers, so the same 429 was graded three different ways depending on which
 * lane saw it.
 *
 * The drift warning that used to sit here said the import "would invert the
 * package graph". It would not: `@developerz/agent` and `apps/runner` both
 * already depend on `@developerz/domain`. The duplication was never load-bearing,
 * and {@link hasLlmExhaustionMarker} is the export that ends it — add a new
 * provider marker HERE, once.
 */
const LLM_EXHAUSTION_MARKERS =
  /insufficient_quota|insufficient.{0,4}(?:credit|balance)|credit balance|exceeded your current quota|quota exceeded|billing hard limit|out of credits|payment required|access_terminated_error|reached[^.]{0,40}usage limit|usage limit reached|purchase credits|limit exhausted|quota[^.]{0,20}exhausted|key limit exceeded|requires more credits|can only afford|free-models-per-day|throttling\.allocationquota|[a-z0-9_-]+:usage-limit\b/i;

/**
 * Does this error body report a wall you CANNOT WAIT OUT — a spent quota, an empty
 * wallet, a plan ceiling — as opposed to a throttle that clears on its own?
 *
 * `quota[^.]{0,20}exhausted` joined the set on 2026-09-11, from a live wall on
 * `tesote`: Alibaba's `Your token-plan 1-week quota has been exhausted. The quota
 * will reset at 09-18 16:26:00 UTC.` matched NOTHING here — `limit exhausted`
 * carries the other noun and `quota exceeded` the other verb — so a seven-day
 * ceiling graded `rate_limited` (60s) on its 429, or nothing at all on any other
 * status. Bounded interior, like `reached[^.]{0,40}usage limit`, so the copula is
 * free to vary and a sentence that merely DESCRIBES a quota ("your plan includes a
 * weekly quota") still matches nothing.
 *
 * The exported half of {@link LLM_EXHAUSTION_MARKERS}, and the seam three lanes
 * needed. Before it, the vocabulary was a bare `const` and the only way in from
 * outside was {@link classifyLlmFailure} called at a HARDCODED `429` — passed by
 * callers whose real status was `400` or `403`, because 429 is the one status this
 * module applies the markers on. That is a fake argument standing in for a
 * question the caller never asked, and it reads wrong at 3am.
 *
 * THE SPLIT IS DELIBERATE AND STAYS. This answers only the BODY question. The
 * caller owns WHICH STATUSES MAY ASK IT, and those lists genuinely differ rather
 * than having drifted:
 *   - here, grading a pool key off a box, only `402`/`429` and `0` (the
 *     `APICallError.statusCode ?? 0` sentinel — see #3280) are reachable;
 *   - `packages/agent`'s lane also sees Anthropic's `400` ("credit balance is too
 *     low") and Kimi's `403`.
 * Absorbing the status lists into one function would force a union that is wrong
 * for both — a `403` is an invalid credential on one lane and a spent plan on the
 * other, and only the caller knows which wire it is holding.
 *
 * Pure and total over any string; `''` is `false`. The pattern carries no `g`
 * flag, so repeated calls cannot disagree through `lastIndex`.
 */
export function hasLlmExhaustionMarker(body: string): boolean {
  // THE ROUTER'S POOL AGGREGATE IS NOT ONE PROVIDER'S BODY (2026-11-05). Its leading clause
  // is the pool's verdict, and when that clause is RECOVERABLE (`N of M accounts … is|are
  // rate limited … | settling a recovery probe`) any `out of credits` later in the sentence
  // is a `N more account out of credits` clause about a DIFFERENT member. The router itself
  // answers such a pool 429 + `Retry-After`, never its 402 (noCandidatesError,
  // multi-ai-router `apps/api/src/services/routing/no-candidates.ts` (not in this repo): "recoverable outranks
  // exhausted"). Reading the member's clause as the verdict cooled live keys for an hour
  // against a reset 30 s out — see {@link ROUTER_POOL_RECOVERABLE}. A genuinely spent pool
  // leads with `all N accounts … are out of credits` and still matches below. Only the
  // text AFTER the clause is the router's member list: a wallet named before it (`quota
  // exceeded; 3 of 3 accounts … rate limited`) is still the fact to report.
  const pool = ROUTER_POOL_RECOVERABLE.exec(body);
  return LLM_EXHAUSTION_MARKERS.test(pool === null ? body : body.slice(0, pool.index));
}

/**
 * The provider refused because THE REQUEST DID NOT FIT — the prompt is larger
 * than the model's context window. A sibling vocabulary to
 * {@link LLM_EXHAUSTION_MARKERS} and kept beside it for the same reason: prose
 * enters this platform at ONE point, so a new provider's wording is one line
 * here rather than a second matcher on whichever lane happened to meet it.
 *
 * IT IS A DIFFERENT FACT FROM EXHAUSTION AND ITS REMEDY IS DIFFERENT. A spent
 * wallet is cleared by waiting or paying; an oversized request is cleared by
 * compacting the context or routing to a larger model, and no amount of either
 * waiting or paying changes it. That is why the two sets stay separate rather
 * than merging into "a wall you cannot wait out".
 *
 * OF THE FIRST FOUR MARKERS TWO CARRY LIVE PROD ROWS AND TWO DO NOT, and saying which
 * is which is the point of listing them (measured 2026-09-01 over
 * `task_run_events` where `kind='error'`, no time bound):
 *   - `context window exceeds limit` — **20 rows**, the dominant signature.
 *     Served verbatim as `invalid params, context window exceeds limit (2013)`
 *     with NO HTTP status: it reaches a caller as a planner error's own message,
 *     which is why the status-less arm of the grader has to ask this too.
 *   - `maximum context length` — **6 rows**, the openrouter phrasing:
 *     *"This endpoint's maximum context length is 1310720 tokens. However, you
 *     requested about 1877027 tokens"*.
 *   - `exceeded model token limit` — **0 rows today**, and in anyway. This is the
 *     signature `TaskFailureKind.RunnerError`'s docblock recorded on 2026-08-29
 *     at one occurrence — *"planner error: Invalid request: Your request exceeded
 *     model token limit: 1048576 (requested: 1050183)"* — and it is no longer in
 *     the table. It is a phrasing a provider we already dial has already served,
 *     which is a different thing from a phrasing we guessed.
 *   - `context_length_exceeded` — **0 rows**, and not prose at all: OpenAI's
 *     machine-readable `error.code`. In because it is a protocol constant — most
 *     of `PROVIDER_WIRE`'s endpoints speak the OpenAI-compatible shape, and a
 *     documented code cannot be reworded the way vendor copy can.
 *
 * The two zero-row entries are the ones to challenge first if this set is ever
 * suspected of over-firing; the asymmetry below is why they are cheap to carry.
 *
 * IT LEANS NARROW, DELIBERATELY, and the asymmetry is what sets that posture.
 * A marker that does not fire leaves the failure exactly where it is today —
 * `runner_error`, the full retry ladder — and loses nothing. A marker that fires
 * wrongly makes a retryable failure TERMINAL on its first occurrence, because
 * that is what this class is budgeted at.
 *
 * Anthropic's `prompt is too long` (0 prod rows) joined in #4240, against that
 * lean, for a named reason: `parseContextOverflow` (`./context-overflow.ts`) reads
 * the window out of it, and it may only parse what this set recognizes — one
 * vocabulary, so the grader and the parser cannot disagree about which errors are
 * overflows. The sentence is Anthropic's documented 400 body, not a guess, and
 * retrying an unchanged oversized prompt on that wire can never succeed.
 *
 * Codex's `exceeds the context window` (#7615) joined for the same kind of reason:
 * it is OpenAI's own refusal sentence ("Your input exceeds the context window of this
 * model"), served through the multi-ai-router when a ChatGPT upstream refuses. The
 * router also maps that refusal to an Anthropic-shaped `invalid_request_error`
 * "prompt is too long: … (context_length_exceeded)", which the two markers above
 * already match. It states no figures, so `parseContextOverflow` answers all-null.
 */
const LLM_CONTEXT_OVERFLOW_MARKERS =
  /context window exceeds limit|exceeds the context window|maximum context length|exceeded model token limit|context_length_exceeded|prompt is too long/i;

/**
 * Does this error body report a request that DID NOT FIT the model's context
 * window — as opposed to a spent quota, a throttle, or a dead credential?
 *
 * The exported half of {@link LLM_CONTEXT_OVERFLOW_MARKERS}, shaped exactly like
 * {@link hasLlmExhaustionMarker} and split from its caller for the same reason:
 * this answers only the BODY question, and the CALLER owns which statuses may
 * ask it. `apps/runner`'s `providerFailureCode` asks it on 4xx bodies (a refusal)
 * and on a status-less planner error's own text (the shape the dominant signature
 * actually arrives in) — never on a 2xx, where the phrase is a model quoting a
 * customer's repo back at us rather than a provider refusing.
 *
 * Pure and total over any string; `''` is `false`. The pattern carries no `g`
 * flag, so repeated calls cannot disagree through `lastIndex`.
 */
export function hasLlmContextOverflowMarker(body: string): boolean {
  return LLM_CONTEXT_OVERFLOW_MARKERS.test(body);
}

/**
 * THE ROUTER'S RECOVERABLE LEADING CLAUSE — the pool's own verdict that a clock fixes it.
 *
 * The producer is ours: multi-ai-router noCandidatesError + recoverableCondition
 * (`apps/api/src/services/routing/no-candidates.ts` (not in this repo)), which leads with the recoverable
 * group whenever there is one and words it in exactly three ways:
 *
 *   - `rate limited or out of quota`
 *   - `rate limited or out of quota, or settling a recovery probe`
 *   - `settling a recovery probe` — a router-side recovery hold, deliberately NOT worded
 *     as quota (router, 2026-10-04).
 *
 * The third was missed until 2026-11-05: `rate limited` alone was the anchor, so the
 * probe-only sentence fell through to {@link LLM_EXHAUSTION_MARKERS} on its
 * `1 more account out of credits` clause. Verbatim, prod `task_run_events`, runs
 * `run_a19f45d6…` and `run_6b615650…` (keys fab4a81a…, 47a29507…, model
 * MiniMax-M3.1-Flash-Preview), both cooled `credits_exhausted` for about an hour:
 *
 *   `Failed after 4 attempts. Last error: 1 of 6 accounts in pool cn-models-team is
 *   settling a recovery probe (minimax); 1 more account out of credits and needs a
 *   top-up (openrouter-glm53-fallback); 4 more do not serve this model — a client change
 *   (…), earliest reset 2026-11-05T00:08:26.748Z (estimated)`
 *
 * WHY PROSE AND NOT THE STATUS, though the router does say it in a machine field (429 +
 * `Retry-After` here, 402 for an all-spent pool): a VENDOR also answers a hard wallet with
 * 429 (MiniMax's 2056, OpenAI's `insufficient_quota`), so the status means "the pool is
 * cooling" only once the speaker is known to be the router — and the `N of M accounts`
 * clause is what identifies it. Every consumer of the reset already reads it from the
 * same sentence ({@link llmThrottleResetAt}).
 */
const ROUTER_POOL_RECOVERABLE =
  /\d+ of \d+ accounts?(?: in pools? \S+)? (?:is|are) (?:rate limited|settling a recovery probe)|accounts? in pool \S+ (?:is|are) (?:rate limited|settling a recovery probe)/i;

/**
 * THE POOL IS COOLING — a throttle the router names as one, and the third
 * sibling vocabulary beside {@link LLM_EXHAUSTION_MARKERS} and
 * {@link LLM_CONTEXT_OVERFLOW_MARKERS}. Kept apart from the exhaustion set for the
 * reason that set's own docblock gives: a throttle CLEARS ON ITS OWN, a spent
 * wallet does not, and the remedies are opposite (wait, versus pay or re-key).
 *
 * Both markers carry live prod rows (#3050, `task_run_events` `kind='error'`,
 * 7 days to 2026-09-03), and both are the box's router speaking, not a vendor:
 *   - `N of N accounts in pool <name> are|is rate limited or out of quota` —
 *     **105 tasks**, 100% of `runner_error` in the 14 hours to 2026-09-03T09:05Z.
 *     The router's pool-level aggregate, served verbatim through the SDK's retry
 *     wrapper as `Failed after 4 attempts. Last error: 2 of 3 accounts in pool
 *     cn-models-team are rate limited or out of quota (zai, minimax), earliest
 *     reset 2026-09-02T18:33:57.869Z (estimated)`. Every one was graded
 *     `runner_error` and charged three attempts inside fifteen minutes, against a
 *     wall the message itself said would move in five.
 *   - `upstream rate limited (http-status:N)` — **2 tasks**, the router relaying
 *     its upstream's 429. Unambiguously a throttle, so the vocabulary says so; it
 *     names no reset, so the grader that hands out the Uncharged budget does NOT
 *     take it (see `datedThrottle` in `apps/runner`'s `providerFailureCode`) — it
 *     stays on the counted ladder until the router dates it.
 *
 * "OR OUT OF QUOTA" IS DELIBERATELY NOT AN EXHAUSTION MARKER. The router's sentence
 * conflates a cooling account with a spent one, and the two facts have opposite
 * remedies — so the sentence is graded by the half it always carries (the pool is
 * cooling, with a reset instant) and never promoted to "buy more". A pool that is
 * genuinely spent keeps reporting a reset that never arrives, which is a hold that
 * re-arms rather than a task that dies, and that is the correct shape for a fact the
 * router cannot state precisely. {@link llmThrottleResetAt} is the instant it does
 * state.
 *
 *   - `1 of 2 accounts is rate limited or out of quota (chatgpt …); 1 more does not
 *     serve this model …, earliest reset 2026-10-22T06:28:25.081Z` — the POOL-LESS
 *     wording (2026-10-17, tesote's router key, ChatGPT's weekly quota spent). Missed
 *     by an `in pool` anchor, it re-drove as transient 1,737 times in 12h. The
 *     `N of M` count keeps it the router's aggregate: no vendor writes that.
 *
 * Matched on `N of M accounts … rate limited | settling a recovery probe` ({@link
 * ROUTER_POOL_RECOVERABLE}, with or without `in pool <name>`)
 * rather than the whole sentence, so a renamed pool or a reworded tail does not
 * silently return the population to `runner_error`; the numbers and the
 * `(zai, minimax)` list are free to vary.
 */
const LLM_THROTTLE_MARKERS = new RegExp(
  `${ROUTER_POOL_RECOVERABLE.source}|upstream rate limited`,
  'i',
);

/**
 * Does this error body report a throttle that CLEARS ON ITS OWN — a pool of
 * accounts cooling behind the router, or the router's upstream pacing it — as
 * opposed to a spent wallet, an oversized request, or a dead credential?
 *
 * Shaped exactly like {@link hasLlmExhaustionMarker}: this answers only the BODY
 * question, and the CALLER owns which statuses may ask it — and, here, whether the
 * prose also DATES its reset ({@link llmThrottleResetAt}). `apps/runner`'s
 * `providerFailureCode` asks it on a 429 body and on a status-less planner error's
 * own text, and grades a dated match `provider_rate_limit` — the kind whose
 * Uncharged budget exists for exactly this, and which had no producer until this
 * marker; an undated one stays on the counted ladder, because the budget's pacer
 * waits on the date.
 *
 * Pure and total over any string; `''` is `false`. No `g` flag, so repeated calls
 * cannot disagree through `lastIndex`.
 */
export function hasLlmThrottleMarker(body: string): boolean {
  return LLM_THROTTLE_MARKERS.test(body);
}

/** The router's own estimate of when the pool clears: `earliest reset <ISO instant> (estimated)`. */
const LLM_THROTTLE_RESET_AT = /earliest reset (\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(?:\.\d+)?Z)/i;

/**
 * The instant a throttled pool says it will clear, as the ISO string the router
 * wrote, or `null` when the body names none (the upstream-429 shape carries no
 * estimate). The consumer decides what to do with it — the box's pull hold clamps
 * it to a floor and a cap — so this parses and never judges. A string that parses
 * but is not a real date is the caller's `Date.parse` NaN to refuse; this only
 * promises the shape.
 */
export function llmThrottleResetAt(body: string): string | null {
  const m = LLM_THROTTLE_RESET_AT.exec(body);
  return m?.[1] ?? null;
}

/**
 * The POOL the router named as cooling, or `null` when the body names none.
 *
 * The same clause {@link LLM_THROTTLE_MARKERS} already anchors on — `N of M accounts
 * in pool <name> are rate limited` — read for its SUBJECT rather than its presence.
 * The name is what a pacer keys on: a box that met this wall on its own `DZ_BACKEND_*`
 * env (a coding lane sent no pool credential) holds no account key to cool, so the
 * pool is the only thing both the failure and the scheduler can name.
 *
 * Trailing punctuation is trimmed because the clause is mid-sentence in every measured
 * body (`…in pool cn-models-team are rate limited or out of quota (minimax, …)`), and a
 * pool key carrying a stray comma matches nothing on the read side.
 */
const LLM_THROTTLE_POOL = /accounts? in pool ([^\s,;.]+)/i;

export function llmThrottlePoolName(body: string): string | null {
  const m = LLM_THROTTLE_POOL.exec(body);
  const name = m?.[1]?.replace(/[.,;:]+$/, '') ?? '';
  return name.length > 0 ? name : null;
}

/**
 * Classify a provider failure into a reassignment reason, or `null` when it is
 * not a key-health failure (a 401/invalid key is the BYOK-lifecycle
 * `markKeyInvalid` path — the key is PAUSED, not rotated past; a 5xx is the
 * provider's fault, not the key's).
 * Pure — the runner calls it on a backend auth failure.
 *
 * `body` is the provider's error text (message and/or response body, concatenated
 * by the caller) and is OPTIONAL: the status alone still decides, exactly as it
 * did, for every caller that has no body to hand. It exists because the status
 * alone LIES on at least one live provider — MiniMax returns 429 for a hard Token
 * Plan ceiling, indistinguishable on the wire from a throttle. Graded
 * `rate_limited`, that key keeps a 60s cooldown, stays in the pool, and the task
 * re-dispatches straight back onto a wall that cannot move; the observed cost was
 * 26 failed tasks on one account in a day, each reported to the customer as a
 * bare "failed".
 *
 * The promotion is one-directional and only for 429 → `credits_exhausted`, OR
 * for `0` (the SDK's absent-statusCode sentinel — `apps/worker/src/consumers/
 * plan-execute-call.ts` passes `err.statusCode ?? 0` to
 * `classifyLlmFailure`). The 0 arm's docblock at the arm itself (#3280) is the
 * restatement; the latter is a SENTINEL, not a status, and a same-shape SDK
 * retry error from MiniMax (`Token Plan usage limit reached (2056)`) earns the
 * same `credits_exhausted` grade an HTTP 429 with the same body would. What a WRONG
 * promotion costs, enumerated, because it is not only a TTL:
 *   - the Redis cooldown: 300 s instead of 60 s (`KEY_COOLDOWN_TTL_SEC`,
 *     `apps/worker/src/dispatch/key-runtime.ts`);
 *   - the KEY ROW: `credits_exhausted` writes `byok_keys.status` (the box's `key_failed`
 *     ingest, `keyRowRetryAt`; the in-process lanes, `keyRowCoolOf`), and an UNDATED one
 *     cools it for `KEY_ROW_UNDATED_WALL_RECHECK_MS` (1 h) before the verify probe
 *     re-checks — while an undated `rate_limited` writes nothing. So a working key
 *     wrongly promoted is out of the pool for up to an hour, and the probe — one billed
 *     call — is what brings it back. It still never marks the key invalid, pauses the
 *     account, or needs a human.
 * A missed promotion costs the fleet an infinite retry loop against a wall (the
 * 2026-10-09 screen outage: ~5 h, every claim dead on one key). That asymmetry is why
 * the grade leans forward — and why each marker above is anchored on WALL prose or a
 * machine token a throttle never sends (`reached … usage limit`, `<upstream>:usage-limit`),
 * never on a bare `limit`, `rate` or `usage limit` that can describe a ceiling.
 */
export function classifyLlmFailure(status: number, body?: string): LlmKeyRowCoolingReason | null {
  if (status === 402) return LlmKeyFailureReason.CreditsExhausted;
  if (status === 429) {
    return body !== undefined && hasLlmExhaustionMarker(body)
      ? LlmKeyFailureReason.CreditsExhausted
      : LlmKeyFailureReason.RateLimited;
  }
  // Status-less arm (#3280): when the caller had no HTTP code to grade on
  // (`status === 0`, the `APICallError.statusCode ?? 0` shape in
  // `apps/worker/src/consumers/plan-execute-call.ts`), ask the body whether it
  // carries an exhaustion marker. The marker set is the ONE place provider prose
  // enters the platform (`LLM_EXHAUSTION_MARKERS` above), so a new wording stays
  // one line there. The narrow shape — only the no-code sentinel — is what keeps
  // a status-bearing failure from being silently rewritten: a 401 still answers
  // null, a 5xx still answers null, and that contract is pinned by the sibling
  // test block in `llm-credential-provider.test.ts`.
  if (status === 0 && body !== undefined && hasLlmExhaustionMarker(body)) {
    return LlmKeyFailureReason.CreditsExhausted;
  }
  return null;
}

/**
 * The `key_failed` STATUS event a runner streams when a pool key dies mid-run
 * (arc 101 slice 03) — a 402/429 the box classified via {@link classifyLlmFailure}.
 * The mothership's event-sink detects exactly this shape ({@link classifyLlmFailure}'s
 * consumer side), cools the key (`key_cooldown:<key_id>`), records it in the task's
 * per-run skip set, and re-enqueues the task so the next claim reassigns to the next
 * key ({@link ResolveLlmInput.skipKeyIds}) — a dead key ROTATES, never fails the task.
 *
 * A pure wire shape, the sibling of `KbLearningsPayloadSchema` /
 * `CapabilitiesDetectedPayloadSchema` (in `task-run-event.ts`): it lives here because
 * the whole key-failure vocabulary ({@link LlmKeyFailureReason} + the classifier)
 * already does. The parse is the TRUST BOUNDARY — a box is untrusted input and
 * `key_id` steers a re-dispatch (which key is cooled + skipped), so a malformed
 * payload is refused at the sink and never rotates a key.
 */
export const LlmKeyFailurePayloadSchema = z.object({
  event: z.literal('key_failed'),
  /** The `byok_keys` row id the run failed on — cooled + added to the task skip set. */
  key_id: z.string().min(1),
  reason: LlmKeyFailureReasonSchema,
  /**
   * Whether the mothership may take the task AWAY from the reporting box (#3040).
   *
   * ABSENT or `true` — the pre-#3040 wire shape, byte-identical for every producer
   * that never wrote the field: cool the key, record the skip, AND re-enqueue the
   * task `→ pending` so the next claim rotates forward. That re-enqueue bumps the
   * task's `lease_epoch`, which FENCES the box that reported the failure.
   *
   * `false` — cool + skip ONLY. The box is still serving the run: it recovered onto
   * its fallback dial, or it is failing the run itself and will say so with its own
   * terminal `error`. The review lane's sample-failure path is the producer — a
   * saturated sibling key must step aside for the rest of the pool without the
   * mothership re-running a review the box already finished (duplicate comments,
   * the customer billed twice on their own key).
   *
   * Optional with NO default on purpose: a default would rewrite what an existing
   * producer's payload means when parsed, and the consumer reads it as
   * `reassign !== false` so an absent field cannot drift from `true`.
   *
   * NOT the in-run replacement channel (plan 102 slice 06b, #4678 decision 7). A box
   * run that wants a NEXT key and wants to keep running asks over the `fleet.req`
   * verb `llm_credential_next` (`@developerz/nats`), and the mothership answers a
   * freshly sealed credential and audits `llm.failover`. `reassign: true` keeps the
   * meaning above: re-enqueue and fence this box out. It never means "hand me
   * another key".
   */
  reassign: z.boolean().optional(),
  /**
   * The provider's STATED reset for this key (plan 102 slice 03 R1), read by the
   * box off the refusal with `parseLlmQuotaResetAt`; the mothership cools the key
   * until it (clamped) instead of a flat hour. Absent when the refusal named no
   * time — the consumer's own backoff applies then.
   *
   * OPTIONAL ONLY FOR VERSION SKEW: runners released before slice 03 never write
   * it. Once `/ship` converges the fleet on a runner that does, this optionality
   * is deleted (the field becomes `.nullable()` — "named no time" stated, never
   * omitted) in the same plan; it is not a legacy path to keep.
   */
  reset_at: z.string().datetime({ offset: true }).optional(),
  /**
   * WHAT THE BOX SAW that earned a `rate_limited` — a provider's refusal, or no answer
   * at all ({@link LlmKeyFailureEvidence}). The two share one reason because they
   * share one cool (owner ruling 2026-09-24), but only a refusal says anything about
   * how many requests the key takes at once: a timeout is the endpoint or the
   * transport, and must not teach a concurrency learner (measured 2026-09-28: all four
   * cools of tesote's z.ai Max key in 27 h were timeouts, and each halved its learned
   * capacity, 16 down to 1).
   *
   * OPTIONAL ONLY FOR VERSION SKEW, exactly like `reset_at`: runners on 0.0.27 never
   * write it, and the consumer reads an absent field as UNATTRIBUTED — never as either
   * member. Once `/ship` converges the fleet on a runner that does, this optionality is
   * deleted in the same plan; it is not a legacy path to keep.
   */
  evidence: LlmKeyFailureEvidenceSchema.optional(),
  /**
   * The provider's OWN error code off the refusal body (`parseLlmProviderErrorCode`),
   * e.g. z.ai's `1302` / `1305`. Only with `evidence: 'provider_refused'`, and only
   * when the body states one — absent otherwise. RECORDED, never branched on.
   * Optional for the same version skew as `evidence`, and for the body that names none.
   */
  provider_code: z.string().min(1).max(LLM_PROVIDER_CODE_MAX_CHARS).optional(),
  /**
   * The MODEL on the key the refusal was about (2026-10-17) — a router key fronts
   * several upstream subs, and a dated quota on one of them parks `(key_id, model)`
   * (`modelParkOf`, `../../entities/byok`) while the key's other models keep serving.
   * Bounded like a catalogue row's `model`. Optional for the version skew of a box
   * that predates it; absent, the consumer has no model to park and cools the key.
   */
  model: z.string().min(1).max(200).optional(),
});
export type LlmKeyFailurePayload = z.infer<typeof LlmKeyFailurePayloadSchema>;
